/**
 * The App component is initial componet of this app and used to authenticat aws congnito user using aws amplify.
 * This componet loads first layout component and layout componet loads differnt components based on defined routes.
 */

import React, { Component } from 'react';
import ReactGA from 'react-ga';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Provider, connect } from 'react-redux';
import { authConfigs } from './config/aws-config';
import { Layout } from './common/layout';
import * as UserActions from './common/user';
import { config } from './config/config';
import { utilService } from './common/utils';
import { authRoutes } from './routes';
import Amplify, { Auth, Hub } from 'aws-amplify';
import store from './store/configureStore';
import './App.css';
import { bindActionCreators } from 'redux';

// Init google analytics
ReactGA.initialize(config.google_analytics_trackingId);

// Init aws auth config
Amplify.configure({
  Auth: authConfigs.Auth,
});

let oauth = authConfigs.oauth;

/**
 * Change redirectSignIn and redirectSignOut url based on incoming host name
 */
if (window.location.hostname === 'places.plc.sydney') {
  oauth.redirectSignIn = config.places_live_url;
  oauth.redirectSignOut = config.redirectSignOutUrl + config.places_live_url;
} else if (window.location.hostname === 'connect.plcarmidale.nsw.edu.au') {
  oauth.redirectSignIn = config.armidale_live_url;
  oauth.redirectSignOut = config.redirectSignOutUrl + config.armidale_live_url;
}

// init hosted ui config
Auth.configure({
  oauth,
});

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      authState: 'loading',
      authData: null,
      authError: null,
      userCalled: false,
      redirectPath: '/',
      isOuterPage: false, // if outer page no need to authenticate user
    };

    /**
     * It is a method of aws amplify to listen auth events.
     */

    Hub.listen('auth', (data) => {
      switch (data.payload.event) {
        case 'signIn':
          this.setState(
            { authState: 'signedIn', authData: data.payload.data },
            function () {
              this.getUserInfo();
            }
          );
          break;
        case 'signIn_failure':
          // this.setState({
          //   authState: "signIn",
          //   authData: null,
          //   authError: data.payload.data,
          // });
          break;
        case 'signOut':
          break;
        default:
          break;
      }
    });
  }

  /**
   * Get aws cognito users data from Auth.currentAuthenticatedUser() method and call api to get current user information from database.
   *
   */

  getUserInfo() {
    if (!this.state.userCalled) {
      this.setState({ userCalled: true }, function () {
        Auth.currentAuthenticatedUser().then((user) => {
          if (localStorage.getItem('redirectPath')) {
            window.location.pathname = localStorage.getItem('redirectPath');
            localStorage.setItem('redirectPath', null);
          }
          const token = user.signInUserSession.idToken.jwtToken;
          // let data = thisObj.getUserCountryName(config.ipstack_access_key);
          this.props.requestUserInfo({
            // country_name: result['country_name'],
            token: token,
            username: user.attributes['custom:User-Name'],
            email: user.attributes.email,
            role: user.attributes['custom:Role'],
            orgUnit: user.attributes['custom:Orgunit'],
            carerNumber: user.attributes['custom:CARER_NUMBER'],
            family_name: user.attributes.family_name,
            given_name: user.attributes.given_name,
            sub: user.attributes.sub,
          });
        });
      });
    }
  }

  /**
   * It is a react lifycycle method which is invoked immediately after a component is mounted
   * It checks current user's session has been expired or not if session expired it redirect to sign in page else fetch details of curret user.
   */
  componentDidMount() {
    // google analytic page view
    utilService.pageView();

    const href = window.location.href;
    // Cognito hosted-UI callbacks carry a code or an implicit token. Amplify's
    // Hub listener (see constructor) handles the session, so skip auth here.
    const isCognitoCallback =
      href.indexOf('?code=') > -1 ||
      (href.indexOf('#access_token') > -1 &&
        href.indexOf('token_type=Bearer&expires_in') > -1);
    const isDocuSignReturn =
      href.indexOf('/docusign/?') > -1 ||
      href.indexOf('/docusignboarder/?') > -1 ||
      href.indexOf('/docusignpk/?') > -1 ||
      href.indexOf('/docusignPK2K12/?') > -1 ||
      href.indexOf('/docusignpkkps/?') > -1 ||
      href.indexOf('/docusignPreschools/?') > -1 ||
      href.indexOf('/docusign_interview/?') > -1;

    if (!isCognitoCallback) {
      if (isDocuSignReturn) {
        this.setState({
          isOuterPage: true,
        });
      } else {
        this.setState(
          {
            redirectPath: window.location.pathname,
          },
          function () {
            Auth.currentAuthenticatedUser({
              bypassCache: true,
            })
              .then((user) => {
                // get user country name
                // let data = thisObj.getUserCountryName(config.ipstack_access_key);

                const token = user.signInUserSession.idToken.jwtToken;
                this.setState({ authState: 'signedIn' });
                this.props.requestUserInfo({
                  // country_name: result["country_name"],
                  token: token,
                  username: user.attributes['custom:User-Name'],
                  email: user.attributes.email,
                  role: user.attributes['custom:Role'],
                  orgUnit: user.attributes['custom:Orgunit'],
                  carerNumber: user.attributes['custom:CARER_NUMBER'],
                  family_name: user.attributes.family_name,
                  given_name: user.attributes.given_name,
                  sub: user.attributes.sub,
                });
              })
              .catch((e) => {
                console.log('Error...', e);
                this.setState({ authState: 'signIn' });
              });
          }
        );
      }
    }
  }

  /**
   * Get user's country name by using ipstack service
   * Url : https://api.ipstack.com/
   * @param {*} access_key ipstack access key
   */
  getUserCountryName(access_key) {
    return new Promise((resolve, reject) => {
      fetch(config.ipstack_api_url + 'check?access_key=' + access_key, {
        method: 'GET',
      })
        .then((result) => result.json())
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          if (error) {
            reject(error);
          }
        });
    });
  }

  /**
   * signIn method is executed when user is not signedIn and redirect to saml idp's login page according to get host name
   */

  signIn() {
    // save redirect path
    localStorage.setItem('redirectPath', this.state.redirectPath);
    if (window.location.hostname === 'places.plc.sydney') {
      Auth.currentSession()
        .then((session) => {
          const token = session.idToken.jwtToken;
          Auth.federatedSignIn({
            token: token,
            provider: 'PLC-Sydney',
          });
        })
        .catch((e) => {
          console.log('Error..', e);
          Auth.federatedSignIn({
            provider: 'PLC-Sydney',
          });
        });
    } else if (window.location.hostname === 'connect.plcarmidale.nsw.edu.au') {
      Auth.currentSession()
        .then((session) => {
          const token = session.idToken.jwtToken;
          Auth.federatedSignIn({
            token: token,
            provider: 'PLC-Armidale',
          });
        })
        .catch((e) => {
          console.log('Error...', e);
          Auth.federatedSignIn({
            provider: 'PLC-Armidale',
          });
        });
    } else {
      Auth.currentSession()
        .then((session) => {
          const token = session.idToken.jwtToken;
          Auth.federatedSignIn({
            token: token,
            provider: 'PLC-Sydney',
          });
        })
        .catch((e) => {
          console.log('Error...', e);
          Auth.federatedSignIn({
            provider: 'PLC-Sydney',
          });
        });
    }
  }

  /**
   * render() is a react lifecycle method.
   * It uses jsx and returns jsx with backend data.
   */
  render() {
    const { authState, isOuterPage } = this.state;
    return (
      <div>
        {!isOuterPage && authState === 'loading' && <div>loading...</div>}
        {!isOuterPage &&
          authState === 'signIn' && <div>loading...</div> &&
          this.signIn()}
        {!isOuterPage && authState === 'signedIn' && (
          <Provider store={store}>
            <Router>
              <Switch>
                <Route path="/" name="Home" component={Layout} />
              </Switch>
            </Router>
          </Provider>
        )}
        {isOuterPage && (
          <Provider store={store}>
            <Router>
              <Switch>
                {authRoutes.map((route, idx) => {
                  return route.component ? (
                    <Route
                      key={idx}
                      path={route.path}
                      exact={route.exact}
                      name={route.name}
                      render={(props) => <route.component {...props} />}
                    />
                  ) : null;
                })}
              </Switch>
            </Router>
          </Provider>
        )}
      </div>
    );
  }
}

/**
 * connect() function connects a react component to a redux store.
 */
export default connect(
  (state) => state.user,
  (dispatch) => bindActionCreators(UserActions, dispatch)
)(App);